
import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { LegalObligation } from "@/services/legalObligationsService";
import { useTenancy } from "@/contexts/TenancyContext";


interface LegalObligationsComboboxProps {
  selected: string[];
  setSelected: (value: string[] | ((prev: string[]) => string[])) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onSelect?: (id: string) => void;
  onDeselect?: (id: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  obligations?: LegalObligation[];
  fetchObligations?: () => Promise<LegalObligation[]>;
}

interface LegalObligationsContextType {
  obligations: LegalObligation[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const LegalObligationsContext = React.createContext<LegalObligationsContextType>({
  obligations: [],
  loading: false,
  error: null,
  refresh: async () => {},
});

export const LegalObligationsProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const [obligations, setObligations] = React.useState<LegalObligation[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getAllLegalObligations } = await import('@/services/legalObligationsService');
      const data = await getAllLegalObligations();
      setObligations(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch legal obligations');
      setError(error);
      console.error('Error fetching legal obligations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <LegalObligationsContext.Provider value={{ obligations, loading, error, refresh }}>
      {children}
    </LegalObligationsContext.Provider>
  );
};

export const useLegalObligations = () => {
  const context = React.useContext(LegalObligationsContext);
  if (!context) {
    throw new Error('useLegalObligations must be used within a LegalObligationsProvider');
  }
  return context;
};

export const LegalObligationsCombobox: React.FC<LegalObligationsComboboxProps> = ({
  selected,
  setSelected,
  placeholder = "Select legal obligations...",
  className,
  fullWidth = false,
  onSelect,
  onDeselect,
  onAllSelect,
  onAllDeselect,
  obligations: propObligations,
  fetchObligations: propFetchObligations,
}) => {
  const { activeTenancy, userIdToName } = useTenancy();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { obligations: contextObligations, loading, error, refresh } = useLegalObligations();
  
  // Use prop obligations if provided, otherwise use context
  const obligations = propObligations || contextObligations || [];
  
  // Filter obligations based on search query
  const filteredObligations = React.useMemo(() => {
    if (!searchQuery) return obligations;
    const query = searchQuery.toLowerCase();
    return obligations.filter(obligation => 
      (obligation.requirement?.toLowerCase().includes(query)) ||
      (obligation.id?.toLowerCase().includes(query)) ||
      (obligation.owner?.toLowerCase().includes(query))
    );
  }, [obligations, searchQuery]);

  // Toggle obligation selection
  const toggleObligation = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(selectedId => selectedId !== id)
      : [...selected, id];
      
    setSelected(newSelected);
    
    // Call appropriate callback
    if (selected.includes(id)) {
      onDeselect?.(id);
    } else {
      onSelect?.(id);
    }
  };

  // Toggle all obligations
  const toggleSelectAll = () => {
    if (selected.length === obligations.length) {
      setSelected([]);
      onAllDeselect?.();
    } else {
      setSelected(obligations.map(obligation => obligation.id));
      onAllSelect?.();
    }
  };

  // Get obligation by ID
  const getObligationById = (id: string) => 
    obligations.find(obligation => obligation.id === id);

  // Format date for display
  const formatDate = (date: Date | string | Object | null) => {
    if (!date) return 'No date';
    try {
      // Handle Firestore Timestamp or plain object with toDate() method
      const dateObj = (date as any)?.toDate?.() || 
                    (date instanceof Date ? date : new Date(date as string));
      return dateObj.toLocaleDateString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Invalid date';
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className, {
      'w-full': fullWidth,
    })}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn("w-full justify-between hover:bg-primary/2", {
                'text-muted-foreground': selected.length === 0,
                'w-full': fullWidth,
              })}
            >
              {selected.length > 0 ? (
                <span className="truncate">
                  {selected.length} obligation{selected.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </div>

        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <div className="px-3 pt-3 pb-2">
              <CommandInput
                placeholder="Search legal obligations..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="h-9"
              />
              
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="hover:text-foreground hover:underline"
                >
                  {selected.length === obligations.length ? 'Deselect all' : 'Select all'}
                </button>
                <span>
                  {selected.length} of {obligations.length} selected
                </span>
              </div>
            </div>
            
            <CommandList 
              className="max-h-[400px] overflow-auto"
              onWheel={(e) => {
                // Prevent wheel events from propagating to parent elements
                e.stopPropagation();
              }}
            >
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading legal obligations...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Failed to load legal obligations. <button onClick={refresh} className="text-primary hover:underline">Retry</button>
                </div>
              ) : filteredObligations.length === 0 ? (
                <CommandEmpty className="py-6">No legal obligations found.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredObligations.map((obligation) => {
                    const isSelected = selected.includes(obligation.id);
                    return (
                      <CommandItem
                        key={obligation.id}
                        value={obligation.id}
                        onSelect={() => toggleObligation(obligation.id)}
                        className="flex items-center justify-between gap-2 cursor-pointer"
                      >
                        <div className="flex items-center">
                          <div className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}>
                            <Check className={cn("h-4 w-4")} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {obligation.requirement || `Obligation ${obligation.id}`}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              Owner: {userIdToName[obligation.owner] || 'Unassigned'} • Updated: {formatDate(obligation.dateUpdated)}
                            </div>
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map((id) => {
            const obligation = getObligationById(id);
            if (!obligation) return null;
            
            return (
              <Badge
                key={id}
                variant="secondary"
                className="pr-1.5 hover:bg-secondary/80 transition-colors"
              >
                <span className="truncate max-w-[200px]">
                  {obligation.requirement || `Obligation ${obligation.id}`}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleObligation(id);
                  }}
                  className="ml-1.5 rounded-full hover:bg-accent flex items-center justify-center w-4 h-4"
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove</span>
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LegalObligationsCombobox;
